<script setup lang="ts">
import QRCode from 'qrcode'
import { computed, nextTick, ref, watch } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { useScreen } from '@/composables/useScreen'

const STUDENT_LOGIN_PATH = '/users/login_as_student'

interface Student {
  id: number
  login_token: string
}

const { notify } = useNotification()
const { isMobile } = useScreen()

const isOpen = ref(false)
const student = ref<Student | null>(null)
const isCopied = ref(false)
const qrcode = ref<HTMLCanvasElement | null>(null)

const url = computed(() => {
  if (!student.value) return null
  const parsedUrl = new URL(STUDENT_LOGIN_PATH, window.location.href)
  parsedUrl.searchParams.set('student_id', String(student.value.id))
  parsedUrl.searchParams.set('token', student.value.login_token)
  return parsedUrl.toString()
})

watch(url, async newUrl => {
  if (!newUrl) return
  await nextTick()
  if (!qrcode.value) return
  QRCode.toCanvas(qrcode.value, newUrl, { width: 250 })
})

watch(isOpen, val => {
  if (val) return
  student.value = null
  isCopied.value = false
})

const open = (studentPayload: Student) => {
  isOpen.value = true
  student.value = studentPayload
}

const copyError = () => {
  notify({ type: 'error', title: 'Не удалось скопировать ссылку.' })
}

const copyLink = async () => {
  if (!url.value) return
  if (!navigator.clipboard) {
    const textarea = document.createElement('textarea')
    textarea.value = url.value
    textarea.style.top = '0'
    textarea.style.left = '0'
    textarea.style.position = 'fixed'
    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()
    try {
      const successful = document.execCommand('copy')
      if (!successful) throw new Error('Copy command failed')
      isCopied.value = true
    } catch (e) {
      console.error(e)
      copyError()
    }
    document.body.removeChild(textarea)
    return
  }
  try {
    await navigator.clipboard.writeText(url.value)
    isCopied.value = true
  } catch (e) {
    console.error(e)
    copyError()
  }
}

defineExpose({ open })
</script>

<template>
  <v-modal v-model="isOpen" title="Войти с другого устройства">
    <div class="student-login-modal">
      <h2 v-if="!isMobile" class="text-h3">Войти с другого устройства</h2>

      <div class="student-login-modal__content">
        <canvas ref="qrcode" class="student-login-modal__qrcode my-2" />

        <p>Отсканируйте этот код для входа в кабинет ребёнка с другого устройства</p>

        <p class="gray-text my-2">или</p>

        <v-button mode="primary large" @click="copyLink">
          <v-icon class="mr-1">mdi-content-copy</v-icon>
          {{ isCopied ? 'Ссылка скопирована' : 'Скопировать ссылку' }}
        </v-button>

        <p class="text-caption my-2">QR-код и ссылка обновляются каждые 24 часа.</p>
      </div>
    </div>
  </v-modal>
</template>

<style lang="scss">
.student-login-modal {
  display: flex;
  flex-direction: column;

  @include desktop {
    width: 450px;
    overflow-y: auto;
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
}
</style>
